/**
 * Pantalla del contador (DISENO_CONTABILIDAD §7.6): subir el libro diario
 * con vista previa, publicar meses a la directiva e historial de cargas.
 */
import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, FileUp, Loader2, XCircle } from "lucide-react";

import {
  Badge,
  Button,
  Card,
  ErrorCarga,
  PageHeader,
  Select,
} from "../../components/ui";
import { useToast } from "../../components/Toast";
import { cantidad, fechaLarga, mesAnio, nombreMes, pesos } from "../../lib/format";
import {
  fetchCargas,
  fetchEjercicios,
  publicar,
  subirLibroDiario,
  type Ejercicio,
  type ResultadoCarga,
} from "./api";

function VistaPrevia({
  archivo,
  r,
  confirmando,
  onConfirmar,
  onCancelar,
}: {
  archivo: File;
  r: ResultadoCarga;
  confirmando: boolean;
  onConfirmar: () => void;
  onCancelar: () => void;
}): JSX.Element {
  if (r.errores.length > 0 || !r.resumen) {
    return (
      <div className="space-y-3" role="alert">
        <p className="flex items-center gap-1.5 font-medium text-rose-700">
          <XCircle className="h-4 w-4" aria-hidden="true" />
          «{archivo.name}» no se puede cargar. Corrige esto en el sistema contable y vuelve a exportar:
        </p>
        <ul className="list-disc space-y-1 pl-5 text-sm text-neutral-800">
          {r.errores.map((e, i) => (
            <li key={i}>
              {e.fila !== null && <span className="tabular-nums text-neutral-600">Fila {e.fila}: </span>}
              {e.mensaje}
            </li>
          ))}
        </ul>
        <Button variant="secondary" onClick={onCancelar}>
          Elegir otro archivo
        </Button>
      </div>
    );
  }
  const s = r.resumen;
  if (s.sin_cambios) {
    return (
      <div className="space-y-3">
        <p className="text-sm text-neutral-800">
          «{archivo.name}» es igual a la carga vigente de {s.anio}: no hay nada que actualizar.
        </p>
        <Button variant="secondary" onClick={onCancelar}>
          Listo
        </Button>
      </div>
    );
  }
  return (
    <div className="space-y-4">
      <p className="flex items-center gap-1.5 font-medium text-emerald-700">
        <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
        «{archivo.name}» se leyó completo y todos los comprobantes cuadran.
      </p>
      <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-4">
        <div>
          <dt className="text-neutral-600">Ejercicio</dt>
          <dd className="font-medium">{s.anio}</dd>
        </div>
        <div>
          <dt className="text-neutral-600">Período del archivo</dt>
          <dd className="font-medium tabular-nums">
            {fechaLarga(s.fecha_desde)} – {fechaLarga(s.fecha_hasta)}
          </dd>
        </div>
        <div>
          <dt className="text-neutral-600">Comprobantes</dt>
          <dd className="font-medium tabular-nums">
            {cantidad(s.comprobantes)}{" "}
            <span className="font-normal text-neutral-600">
              ({Object.entries(s.por_tipo)
                .map(([t, n]) => `${t} ${cantidad(n)}`)
                .join(" · ")})
            </span>
          </dd>
        </div>
        <div>
          <dt className="text-neutral-600">Líneas · total debe</dt>
          <dd className="font-medium tabular-nums">
            {cantidad(s.lineas)} · {pesos(s.total_debe)}
          </dd>
        </div>
      </dl>
      {s.hay_carga_anterior ? (
        <p className="text-sm text-neutral-800">
          Frente a la carga vigente: <strong>{cantidad(s.agregados)}</strong> comprobantes nuevos o
          modificados y <strong>{cantidad(s.quitados)}</strong> que dejan de estar (un comprobante
          modificado cuenta en ambos). Al confirmar, el ejercicio {s.anio} se reemplaza completo.
        </p>
      ) : (
        <p className="text-sm text-neutral-800">Es la primera carga del ejercicio {s.anio}.</p>
      )}
      {s.cuentas_nuevas.length > 0 && (
        <details className="text-sm">
          <summary className="cursor-pointer text-brand-700 hover:text-brand-800">
            {cantidad(s.cuentas_nuevas.length)} cuentas nuevas en el plan (
            {s.cuentas_nuevas.filter((c) => !c.tipo).length} sin clasificación propuesta)
          </summary>
          <ul className="mt-2 max-h-60 overflow-y-auto">
            {s.cuentas_nuevas.map((c) => (
              <li key={c.codigo} className="flex gap-3 py-0.5">
                <span className="w-28 shrink-0 tabular-nums text-neutral-600">{c.codigo}</span>
                <span className="flex-1">{c.nombre}</span>
                {!c.tipo && <Badge tone="amber">Sin clasificar</Badge>}
              </li>
            ))}
          </ul>
        </details>
      )}
      <p className="text-sm text-neutral-600">
        Cargar no publica nada nuevo: la directiva sigue viendo lo publicado hasta que lo cambies abajo.
      </p>
      <div className="flex gap-2">
        <Button onClick={onConfirmar} disabled={confirmando}>
          {confirmando && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
          Confirmar carga
        </Button>
        <Button variant="secondary" onClick={onCancelar} disabled={confirmando}>
          Cancelar
        </Button>
      </div>
    </div>
  );
}

function Publicacion({ ejercicio }: { ejercicio: Ejercicio }): JSX.Element {
  const toast = useToast();
  const qc = useQueryClient();
  const mesActual = ejercicio.publicado_hasta ? Number(ejercicio.publicado_hasta.slice(5, 7)) : 0;
  const [mes, setMes] = useState(mesActual);
  const [provisorio, setProvisorio] = useState(ejercicio.mostrar_provisorio);
  const ultimoMes = ejercicio.ultimo_dato ? Number(ejercicio.ultimo_dato.slice(5, 7)) : 12;
  const m = useMutation({
    mutationFn: () =>
      publicar(ejercicio.anio, {
        publicado_hasta: mes ? `${ejercicio.anio}-${String(mes).padStart(2, "0")}-01` : null,
        mostrar_provisorio: provisorio,
      }),
    onSuccess: () => {
      toast.success("Publicación actualizada");
      void qc.invalidateQueries({ queryKey: ["contabilidad"] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "No se pudo publicar"),
  });
  const cambio = mes !== mesActual || provisorio !== ejercicio.mostrar_provisorio;
  return (
    <div className="space-y-3">
      <p className="text-sm text-neutral-800">
        Hoy la directiva ve {ejercicio.anio}{" "}
        {ejercicio.publicado_hasta ? (
          <>
            cerrado hasta <strong>{mesAnio(ejercicio.publicado_hasta)}</strong>
          </>
        ) : (
          <strong>sin meses cerrados</strong>
        )}
        {ejercicio.mostrar_provisorio ? " y lo posterior como provisorio." : " y nada posterior."}
      </p>
      <div className="flex flex-wrap items-end gap-3">
        <div className="w-48">
          <Select label="Publicar hasta" value={String(mes)} onChange={(e) => setMes(Number(e.target.value))}>
            <option value="0">Nada publicado</option>
            {Array.from({ length: ultimoMes }, (_, i) => i + 1).map((n) => (
              <option key={n} value={n}>
                {nombreMes(n)}
              </option>
            ))}
          </Select>
        </div>
        <label className="flex items-center gap-2 pb-2 text-sm text-neutral-800">
          <input
            type="checkbox"
            checked={provisorio}
            onChange={(e) => setProvisorio(e.target.checked)}
            className="h-4 w-4 rounded border-neutral-450 text-brand-700 focus-visible:ring-2 focus-visible:ring-brand-500"
          />
          Mostrar los meses siguientes como provisorios
        </label>
        <Button onClick={() => m.mutate()} disabled={!cambio || m.isPending}>
          Guardar
        </Button>
      </div>
    </div>
  );
}

export function CargasPage(): JSX.Element {
  const toast = useToast();
  const qc = useQueryClient();
  const input = useRef<HTMLInputElement>(null);
  const [archivo, setArchivo] = useState<File | null>(null);
  const cargas = useQuery({ queryKey: ["contabilidad", "cargas"], queryFn: fetchCargas });
  const ejercicios = useQuery({ queryKey: ["contabilidad", "ejercicios"], queryFn: fetchEjercicios });

  const previa = useMutation({
    mutationFn: (f: File) => subirLibroDiario(f, false),
    onError: (e) => toast.error(e instanceof Error ? e.message : "No se pudo leer el archivo"),
  });
  const confirmar = useMutation({
    mutationFn: (f: File) => subirLibroDiario(f, true),
    onSuccess: (r) => {
      if (r.confirmado) {
        toast.success(`Libro diario ${r.resumen?.anio ?? ""} cargado`);
        void qc.invalidateQueries({ queryKey: ["contabilidad"] });
      } else {
        toast.error("El archivo no se cargó: revisa la vista previa");
      }
      setArchivo(null);
      previa.reset();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "No se pudo cargar el archivo"),
  });

  const elegir = (f: File | undefined): void => {
    if (!f) return;
    setArchivo(f);
    previa.mutate(f);
  };
  const cancelar = (): void => {
    setArchivo(null);
    previa.reset();
    if (input.current) input.current.value = "";
  };

  return (
    <div className="mx-auto max-w-5xl space-y-5">
      <PageHeader
        title="Cargas y publicación"
        subtitle="Sube el libro diario exportado a Excel desde el sistema contable. Cada carga reemplaza el año completo, así que puedes subir el mismo archivo acumulado todas las veces que quieras."
      />

      <Card padding="md" className="space-y-4">
        <h2 className="font-medium text-neutral-900">Subir libro diario</h2>
        {!archivo && (
          <label
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              elegir(e.dataTransfer.files[0]);
            }}
            className="flex cursor-pointer flex-col items-center gap-2 rounded-lg border-2 border-dashed border-neutral-300 px-4 py-8 text-center text-sm text-neutral-600 hover:border-brand-500 hover:bg-brand-50/40 focus-within:ring-2 focus-within:ring-brand-500">
            <FileUp className="h-6 w-6 text-brand-700" aria-hidden="true" />
            <span>
              <span className="font-medium text-brand-700">Elige el archivo .xlsx</span> o arrástralo aquí
            </span>
            <input
              ref={input}
              type="file"
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              className="sr-only"
              onChange={(e) => elegir(e.target.files?.[0])}
            />
          </label>
        )}
        {archivo && previa.isPending && (
          <p className="flex items-center gap-2 text-sm text-neutral-600" role="status">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Leyendo «{archivo.name}»…
          </p>
        )}
        {archivo && previa.data && (
          <VistaPrevia
            archivo={archivo}
            r={previa.data}
            confirmando={confirmar.isPending}
            onConfirmar={() => confirmar.mutate(archivo)}
            onCancelar={cancelar}
          />
        )}
        {archivo && previa.isError && (
          <Button variant="secondary" onClick={cancelar}>
            Elegir otro archivo
          </Button>
        )}
      </Card>

      <Card padding="md" className="space-y-4">
        <h2 className="font-medium text-neutral-900">Publicación a la directiva</h2>
        {ejercicios.isError && <ErrorCarga error={ejercicios.error} onReintentar={() => void ejercicios.refetch()} />}
        {ejercicios.data?.length === 0 && (
          <p className="text-sm text-neutral-600">Sube un libro diario para poder publicar.</p>
        )}
        {ejercicios.data?.map((e) => (
          <div key={`${e.id}-${e.publicado_hasta ?? ""}-${String(e.mostrar_provisorio)}`}>
            {ejercicios.data.length > 1 && <h3 className="mb-2 text-sm font-medium">{e.anio}</h3>}
            <Publicacion ejercicio={e} />
          </div>
        ))}
      </Card>

      <Card padding="none">
        <h2 className="px-4 pt-4 font-medium text-neutral-900">Historial de cargas</h2>
        {cargas.isError && <ErrorCarga error={cargas.error} onReintentar={() => void cargas.refetch()} />}
        <div className="overflow-x-auto">
          <table className="mt-3 w-full min-w-[720px] text-sm">
            <thead className="bg-neutral-100 text-xs uppercase tracking-wide text-neutral-600">
              <tr>
                <th className="px-3 py-2 text-left font-medium">Subida</th>
                <th className="px-3 py-2 text-left font-medium">Archivo</th>
                <th className="px-3 py-2 text-left font-medium">Por</th>
                <th className="px-3 py-2 text-left font-medium">Trae</th>
                <th className="px-3 py-2 text-right font-medium">Comprobantes</th>
                <th className="px-3 py-2 text-right font-medium">Cambios</th>
                <th className="px-3 py-2 text-left font-medium">Estado</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-200">
              {cargas.data?.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-3 py-6 text-center text-neutral-600">
                    Todavía no hay cargas.
                  </td>
                </tr>
              )}
              {cargas.data?.map((c) => (
                <tr key={c.id}>
                  <td className="whitespace-nowrap px-3 py-1.5 tabular-nums">
                    {fechaLarga(c.created_at)}{" "}
                    {new Date(c.created_at).toLocaleTimeString("es-CL", { hour: "2-digit", minute: "2-digit" })}
                  </td>
                  <td className="px-3 py-1.5">{c.archivo_nombre}</td>
                  <td className="px-3 py-1.5">{c.usuario_nombre ?? "—"}</td>
                  <td className="whitespace-nowrap px-3 py-1.5 tabular-nums">
                    {fechaLarga(c.fecha_desde)} – {fechaLarga(c.fecha_hasta)}
                  </td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{cantidad(c.comprobantes)}</td>
                  <td className="whitespace-nowrap px-3 py-1.5 text-right tabular-nums">
                    +{cantidad(c.agregados)} / −{cantidad(c.quitados)}
                  </td>
                  <td className="px-3 py-1.5">
                    {c.estado === "vigente" ? (
                      <Badge tone="emerald">Vigente</Badge>
                    ) : (
                      <Badge tone="slate">Reemplazada</Badge>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <p className="text-sm text-neutral-600">
        Después de cargar, revisa las cuentas nuevas en{" "}
        <Link to="/contabilidad/plan?filtro=por_revisar" className="text-brand-700 hover:text-brand-800">
          Plan de cuentas
        </Link>
        .
      </p>
    </div>
  );
}
